import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getMembers, memberPhotoUrl } from "../../services/memberService"
import { Plus, Users, Filter, Download, MoreHorizontal, Eye, Pencil, Trash2 } from "lucide-react"
import { Button } from "../../components/ui/Button"
import { SearchInput } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import { Card } from "../../components/ui/Card"
import { Badge } from "../../components/ui/Badge"
import { Avatar } from "../../components/ui/Avatar"
import { EmptyState } from "../../components/ui/EmptyState"
import { TableSkeleton } from "../../components/ui/Skeleton"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/Table"
import { fr } from "../../locales/fr"
import { subscriptionStatusLabel } from "../../locales/fr"

export default function MembersList() {
  const navigate = useNavigate()
  const [members, setMembers] = useState([])
  const [filteredMembers, setFilteredMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [openDropdown, setOpenDropdown] = useState(null)

  useEffect(() => {
    loadMembers()
  }, [])

  useEffect(() => {
    filterMembers()
  }, [members, search, statusFilter])

  const loadMembers = async () => {
    setLoading(true)
    try {
      const data = await getMembers()
      setMembers(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error("Failed to load members", e)
    }
    setLoading(false)
  }

  const filterMembers = () => {
    let result = [...members]

    if (search) {
      const q = search.toLowerCase()
      result = result.filter((m) => {
        const hay = [
          m.name,
          m.firstName,
          m.lastName,
          m.email,
          m.phone,
          m.memberNumber,
          m.cin,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        return hay.includes(q)
      })
    }

    if (statusFilter !== "all") {
      result = result.filter((m) => m.status === statusFilter)
    }

    setFilteredMembers(result)
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge variant="success">{fr.status.active}</Badge>
      case "inactive":
        return <Badge variant="secondary">{fr.status.inactive}</Badge>
      case "expired":
        return <Badge variant="destructive">{fr.status.expired}</Badge>
      default:
        return <Badge variant="outline">{subscriptionStatusLabel(status)}</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{fr.members.title}</h1>
          <p className="text-muted-foreground">{fr.members.subtitle}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {fr.common.export}
          </Button>
          <Button onClick={() => navigate("/subscriptions/new")}>
            <Plus className="mr-2 h-4 w-4" />
            {fr.common.addMember}
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput
              placeholder={fr.members.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-44"
            >
              <option value="all">{fr.status.all}</option>
              <option value="active">{fr.status.active}</option>
              <option value="inactive">{fr.status.inactive}</option>
              <option value="expired">{fr.status.expired}</option>
            </Select>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <TableSkeleton rows={8} columns={6} />
        ) : filteredMembers.length === 0 ? (
          <EmptyState
            icon={Users}
            title={fr.members.emptyTitle}
            description={
              search || statusFilter !== "all"
                ? fr.members.emptyFiltered
                : fr.members.emptyInitial
            }
            action={() => navigate("/subscriptions/new")}
            actionLabel={fr.common.addMember}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{fr.members.table.member}</TableHead>
                <TableHead>{fr.members.table.email}</TableHead>
                <TableHead>{fr.members.table.subscription}</TableHead>
                <TableHead>{fr.members.table.status}</TableHead>
                <TableHead>{fr.members.table.joinDate}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar
                        src={memberPhotoUrl(member.photo)}
                        fallback={(member.name || member.firstName || "?").charAt(0)}
                        alt={member.name}
                        size="md"
                      />
                      <div>
                        <p className="font-medium text-foreground">{member.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {member.memberNumber || `${fr.common.id} : ${member.id}`}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{member.email || "—"}</span>
                  </TableCell>
                  <TableCell>
                    <div className="text-foreground">
                      <span>{member.subscription || "—"}</span>
                      {member.subscriptionEnd && (
                        <p className="text-xs text-muted-foreground">
                          {fr.memberDetail.subscriptionUntil} {member.subscriptionEnd}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(member.status)}</TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{member.joinDate || "—"}</span>
                  </TableCell>
                  <TableCell>
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setOpenDropdown(openDropdown === member.id ? null : member.id)}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                      {openDropdown === member.id && (
                        <div className="absolute right-0 top-full z-10 mt-1 w-44 rounded-md border border-border bg-popover p-1 shadow-lg">
                          <button
                            onClick={() => {
                              navigate(`/members/${member.id}`)
                              setOpenDropdown(null)
                            }}
                            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-foreground hover:bg-secondary"
                          >
                            <Eye className="h-4 w-4" />
                            {fr.common.view}
                          </button>
                          <button
                            onClick={() => {
                              navigate(`/members/edit/${member.id}`)
                              setOpenDropdown(null)
                            }}
                            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-foreground hover:bg-secondary"
                          >
                            <Pencil className="h-4 w-4" />
                            {fr.common.edit}
                          </button>
                          <button
                            onClick={() => setOpenDropdown(null)}
                            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                            {fr.common.delete}
                          </button>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {filteredMembers.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-4">
            <p className="text-sm text-muted-foreground">
              {fr.pagination.members(filteredMembers.length, members.length)}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                {fr.common.previous}
              </Button>
              <Button variant="outline" size="sm" disabled>
                {fr.common.next}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
